import { Store } from 'mutent'
import { compileFilterQuery } from 'mql-match'
import { isEqual, set } from 'lodash'
import { Observable, from, map } from 'rxjs'
import { Page } from '../models/util.model'

/**
 * Simple array adapter for mutent store
 */
class StoreAdapter {
  private fieldKey = '_id'

  constructor(private array: any[] = []) {
    this.array = array
  }

  read(key: string) {
    return this.array.find((i) => i[this.fieldKey] === key)
  }

  find(filters: any) {
    return this.array.find(this.compileFilters(filters))
  }

  filter(filters: any) {
    return this.array.filter(this.compileFilters(filters))
  }

  create(data: any) {
    this.array.push(data)
  }

  update(oldData: any, newData: any) {
    this.array.splice(
      this.array.findIndex((entity) => entity === oldData),
      1,
      newData,
    )
  }

  delete(data: any) {
    this.array.splice(
      this.array.findIndex((entity) => entity === data),
      1,
    )
  }

  /**
   * Filters compilation
   * @param filters - the API filters
   * @returns the array filter predicate
   */
  private compileFilters(filters: any): (item: any) => boolean {
    // Check filters shape
    if (typeof filters !== 'object') {
      return (item) => isEqual(item, filters)
    }

    // Prepare mongo filters object
    const mongoFilter = {}

    for (const key of Object.keys(filters)) {
      if (Array.isArray(filters[key])) {
        set(mongoFilter, `${key}.$in`, filters[key])
      } else {
        set(mongoFilter, key, filters[key])
      }
    }

    return compileFilterQuery(mongoFilter)
  }
}

/**
 * Abstract service stub useful for all services tests.
 * This class must be extended from the service stub to use all its feature.
 * See BrandsServiceStub implementation
 */
export abstract class ServiceStub<Entity> {
  private store: Store<{ entity: Entity }>

  constructor(items: Entity[]) {
    this.store = new Store<{ entity: Entity }>({
      adapter: new StoreAdapter(items),
    })
  }

  /**
   * Create a new item
   * @param newItem - The item to create
   * @returns The observable<T> for create the item
   */
  protected _create$(newItem: Entity): Observable<Entity> {
    return from(this.store.create(newItem).unwrap())
  }

  /**
   * Read an item by ID
   * @param itemId - The item ID
   * @returns The observable<T> for read the item
   */
  protected _read$(itemId: string): Observable<Entity> {
    return from(this.store.read(itemId).unwrap())
  }

  /**
   * Update an item by ID
   * @param itemId - The item ID
   * @param item - The item body to update
   * @returns The observable<T> for update the item
   */
  protected _update$(itemId: string, body: any): Observable<Entity> {
    return from(this.store.read(itemId).update(body).unwrap())
  }

  /**
   * Create or update an item
   * @param item - The item body to update
   * @returns The observable<T> for update the item
   */
  protected _upsert$(body: any): Observable<Entity> {
    return body._id ? this._update$(body._id, body) : this._create$(body)
  }

  /**
   * Delete an item by ID
   * @param itemId - The item ID
   * @returns The observable<T> for delete the item
   */
  protected _delete$(itemId: string): Observable<Entity> {
    return from(this.store.read(itemId).delete().unwrap())
  }

  /**
   * Search items by params:
   * @param params - The search params
   * @returns The observable<Page<Entity>> for search items
   */
  protected _search$(params?: any): Observable<Page<Entity>> {
    return from(this.store.filter(params).unwrap()).pipe(
      map((items) => ({ data: items, totalCount: items.length })),
    )
  }

  /**
   * List items by params
   * @param params - The search params
   * @returns The observable<Entity[]> for list items
   */
  protected _list$(params?: any): Observable<Entity[]> {
    return from(this.store.filter(params).unwrap())
  }
}
